import { apiClient } from "~/services/api-client";
import { ApiError, isAxiosError } from "~/shared/utils/api-error";
import type { PostedOrder, PostedOrdersApiResponse, PostedOrdersPagination, PostOrdersToNavResult } from "./types";

export type PostOrdersToNavRequest = {
  orderIds: string[]; // Array of order IDs (SM numbers)
};

export type PostOrdersToNavResponse = {
  success: boolean;
  message?: string;
  data: PostOrdersToNavResult;
};

/**
 * Post selected orders to NAV
 * @param orderIds Array of order IDs (SM numbers) to post
 */
export async function postOrdersToNav(orderIds: string[]): Promise<PostOrdersToNavResult> {
  try {
    if (!orderIds || orderIds.length === 0) {
      throw new ApiError("At least one order ID is required", 400);
    }

    const response = await apiClient.post<PostOrdersToNavResponse>(
      "/posted-orders",
      { orderIds }
    );

    // Both success:true (partial) and success:false (complete failure)
    // return structured data with errors — return it so the UI can display details
    if (!response.data.data) {
      throw new ApiError(
        response.data.message ?? "Failed to post orders to NAV",
        response.status,
        response.data
      );
    }

    return response.data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const statusCode = error.response?.status;
      const responseData = error.response?.data as
        | { message?: string }
        | undefined;
      const message =
        responseData?.message ??
        error.message ??
        "Failed to post orders to NAV";

      throw new ApiError(message, statusCode, error.response?.data);
    }

    if (error instanceof ApiError) {
      throw error;
    }

    throw new ApiError(
      error instanceof Error ? error.message : "An unexpected error occurred",
      undefined,
      error
    );
  }
}

export type PostedOrderFilterParams = {
  cities?: string[];
  locationCodes?: string[];
};

function buildPostedOrderParams(
  page: number,
  limit: number,
  search?: string,
  filters?: PostedOrderFilterParams
): Record<string, unknown> {
  const params: Record<string, unknown> = { page, limit };
  if (search) params.search = search;
  if (filters?.cities?.length) params.cities = filters.cities.join(',');
  if (filters?.locationCodes?.length) params.locationCodes = filters.locationCodes;
  return params;
}

/**
 * Fetch posted orders from the API
 */
export async function fetchPostedOrders(
  page = 1,
  limit = 10,
  search?: string,
  filters?: PostedOrderFilterParams
): Promise<{ items: PostedOrder[]; pagination: PostedOrdersPagination; contextualCities: string[] }> {
  try {
    const response = await apiClient.get<PostedOrdersApiResponse>(
      "/posted-orders",
      { params: buildPostedOrderParams(page, limit, search, filters) }
    );

    if (!response.data) {
      throw new ApiError("Empty response from API", response.status);
    }

    if (!response.data.success) {
      throw new ApiError(
        response.data.message ?? "API request was not successful",
        response.status,
        response.data
      );
    }

    const defaultPagination: PostedOrdersPagination = {
      total: 0,
      page,
      limit,
      totalPages: 0,
    };

    return {
      items: response.data.data ?? [],
      pagination: response.data.pagination ?? defaultPagination,
      contextualCities: response.data.filtersContextual?.cities ?? [],
    };
  } catch (error) {
    if (isAxiosError(error)) {
      const statusCode = error.response?.status;
      const responseData = error.response?.data as
        | { message?: string }
        | undefined;
      const message =
        responseData?.message ??
        error.message ??
        "Failed to fetch posted orders from API";

      throw new ApiError(message, statusCode, error.response?.data);
    }

    if (error instanceof ApiError) {
      throw error;
    }

    throw new ApiError(
      error instanceof Error ? error.message : "An unexpected error occurred",
      undefined,
      error
    );
  }
}
